import { useState } from "react";
import { Link } from "react-router-dom";
import { VEHICLE_TYPE_LABELS } from "@garage/shared";
import {
  useArchiveVehicle,
  useRestoreVehicle,
  useVehicles,
  type VehicleListItem,
} from "../api/vehicles";
import { ExpiryPill } from "../components/ExpiryPill";
import { Alert, Button, Card, Spinner } from "../components/ui";
import { formatINR, formatKm } from "../lib/format";

export default function GaragePage() {
  const [showArchived, setShowArchived] = useState(false);
  const { data, isPending, error } = useVehicles(showArchived);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Garage</h1>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          <Link to="/vehicles/new">
            <Button>Add vehicle</Button>
          </Link>
        </div>
      </div>

      <div className="mt-6">
        {isPending && <Spinner label="Loading your garage" />}
        {error && <Alert>{(error as Error).message}</Alert>}

        {data && data.vehicles.length === 0 && (
          <Card className="p-8 text-center">
            <p className="text-ink-2">
              No vehicles yet. Add one and start logging what you spend on it.
            </p>
            <Link to="/vehicles/new" className="mt-4 inline-block">
              <Button>Add your first vehicle</Button>
            </Link>
          </Card>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {data?.vehicles.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      </div>
    </div>
  );
}

function VehicleCard({ vehicle: v }: { vehicle: VehicleListItem }) {
  const archive = useArchiveVehicle();
  const restore = useRestoreVehicle();
  const title = v.nickname || [v.make, v.model].filter(Boolean).join(" ") || v.regNumber;

  return (
    <Card className={`p-4 ${v.archivedAt ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={`/vehicles/${v.id}`}
            className="text-lg font-medium hover:text-signal"
          >
            {title}
          </Link>
          <p className="font-mono text-xs tracking-wider text-ink-3">
            {v.regNumber}
            <span className="mx-2 opacity-40">·</span>
            {VEHICLE_TYPE_LABELS[v.type]}
            {v.year ? ` · ${v.year}` : ""}
          </p>
        </div>
        {v.archivedAt && (
          <span className="rounded border border-line px-2 py-0.5 font-mono text-[11px] text-ink-3">
            archived
          </span>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2 text-sm">
        <Stat label="Spent" value={formatINR(v.totalSpend)} />
        <Stat label="Services" value={String(v.serviceCount)} />
        <Stat label="Odometer" value={formatKm(v.odometerKm)} />
      </dl>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <ExpiryPill label="PUC" date={v.pucExpiry} />
        <ExpiryPill label="Insurance" date={v.insuranceExpiry} />
        <ExpiryPill label="Fitness" date={v.fitnessExpiry} />
      </div>

      <div className="mt-4 flex gap-2 border-t border-line pt-3">
        <Link to={`/vehicles/${v.id}/edit`} className="flex-1">
          <Button variant="ghost" className="w-full !py-1.5 text-sm">
            Edit
          </Button>
        </Link>
        {v.archivedAt ? (
          <Button
            variant="ghost"
            className="!py-1.5 text-sm"
            disabled={restore.isPending}
            onClick={() => restore.mutate(v.id)}
          >
            Restore
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="!py-1.5 text-sm"
            disabled={archive.isPending}
            onClick={() => archive.mutate(v.id)}
            title="Keeps the service history"
          >
            Archive
          </Button>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10px] tracking-[0.1em] text-ink-3 uppercase">
        {label}
      </dt>
      <dd className="mt-0.5 tabular-nums">{value}</dd>
    </div>
  );
}
